import { LoadingConstants, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { PageWrapper } from '@zg-rentals/rental-revenue-components';
import { GuestPayFooter } from './GuestPayFooter';
import {
  DetailsPageWrapperConfig,
  DetailsPageWrapperConfigContent,
  InvoiceSummaryAmountList,
  StyledPaymentFormContainer,
  StyledSummaryContainer,
} from './GuestPayPaymentDetails';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormField,
  Heading,
  Label,
  LabeledControl,
  Page,
  Spinner,
  Text,
} from '@zillow/constellation-10';
import billingUtils from '../../utils/billingUtils';
import { useHistory } from 'react-router-dom';
import { manifest } from '../../app/routes';
import { useAppGuestPay } from '../../hooks/useGuestPay';
import { StyledAlertText } from '../../components/BillingPaymentMethods';
import type {
  RentalManagerUserMetroidAlicornGuestPaymentDto,
  UserZuoraPaymentMethodDto,
} from '@zg-rentals/ts-adproducts-client';
import {
  AdproductsGuestBillingInvoiceApiHandler_getPaymentMethodDetails,
  AdproductsUserBillingInvoicePaymentApiHandler_makeMultiPaymentForGuestInvoices,
} from '@zg-rentals/ts-adproducts-client';
import {
  emitGuestPaySubmitAcceptTermsEvent,
  emitGuestPaySubmitBackEvent,
  emitGuestPaySubmitPaymentEvent,
} from '../../model/GuestPayClickstreamEvents';

import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

const PAYMENT_INFO_SUBTITLE = `Check that your payment information is correct, authorize the payment, and submit. 
  Credit card payments should process almost immediately. ACH payments can take five to eight 
  business days to process — we’ll contact you if there are any issues.`;

const ACH_TERMS = `By selecting “Submit payment,” I authorize Zillow, Inc. to electronically debit the bank account 
  provided above (my “Bank Account”) for the total payment amount, and to debit or credit my Bank Account to correct
  any errors, if necessary. I understand that Zillow, Inc. may not process payments submitted on a holiday or weekend 
  until the next business day and that it may take several days for a payment to process.`;

const CC_TERMS = `By selecting “Submit payment,” I authorize Zillow, Inc. to charge the credit or debit card provided 
  above (my "Payment Card") for the total payment amount, and to charge or credit my Payment Card to correct any errors, 
  if necessary. I understand that Zillow, Inc. may not process payments submitted on a holiday or weekend until the next 
  business day and that it may take several days for a payment to process.`;

const CC_PAYMENT_TYPE = 'CreditCard';
const ACH_PAYMENT_TYPE = 'ACH';

interface TermsAndConditionsProps {
  setAcceptedTerms: (checked: boolean) => void;
  paymentType: string;
  disabled?: boolean;
  user: ZrmClickstreamUser;
}

const TermsAndConditions = ({ setAcceptedTerms, paymentType, disabled = false, user }: TermsAndConditionsProps) => {
  return (
    <Box>
      <LabeledControl
        label={<Label>{paymentType === ACH_PAYMENT_TYPE ? ACH_TERMS : CC_TERMS}</Label>}
        control={<Checkbox disabled={disabled} />}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const checked = e.target.checked;
          if (checked) {
            emitGuestPaySubmitAcceptTermsEvent({ user });
          }
          setAcceptedTerms(checked);
        }}
      />
    </Box>
  );
};

export const GuestPaySubmitPayment = () => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentMethodState, setPaymentMethodState] = useState<LoadingConstants>(LoadingConstants.INITIAL);
  const [paymentMethod, setPaymentMethod] = useState<UserZuoraPaymentMethodDto>();
  const [paymentInProgress, setPaymentInProgress] = useState(false);

  const history = useHistory();
  const { guestPayState, updateGuestPayState } = useAppGuestPay();
  const { user } = useRentalManagerUser();

  const disableNextButton = !paymentMethod || !guestPayState.paymentMethodId || paymentInProgress || !acceptedTerms;

  useEffect(() => {
    setPaymentMethodState(LoadingConstants.LOADING);
    AdproductsGuestBillingInvoiceApiHandler_getPaymentMethodDetails({
      guestPaySessionId: guestPayState.guestPaySessionKey,
    })
      .then((paymentMethod) => {
        setPaymentMethod(paymentMethod);
        return setPaymentMethodState(LoadingConstants.SUCCESS);
      })
      .catch(() => setPaymentMethodState(LoadingConstants.ERROR));
  }, [guestPayState.guestPaySessionKey]);

  const isLoading = paymentMethodState === LoadingConstants.LOADING || paymentMethodState === LoadingConstants.INITIAL;
  const errorLoadingPaymentMethods = paymentMethodState === LoadingConstants.ERROR;
  const successLoadingPayments = paymentMethodState === LoadingConstants.SUCCESS;

  if (errorLoadingPaymentMethods && !errorMessage)
    setErrorMessage('We are currently unable to load your payment method. Please try again later.');

  const formatPaymentMethod = (paymentMethod: UserZuoraPaymentMethodDto) => {
    if (paymentMethod.paymentType === CC_PAYMENT_TYPE) {
      return `• ${paymentMethod.brand || 'Credit Card'} ending with 
            ${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)} `;
    }
    if (paymentMethod.paymentType === ACH_PAYMENT_TYPE) {
      return `• ${paymentMethod.bankName || 'Bank'} ending with 
            ${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)} `;
    }
    return 'Unknown Payment Method';
  };

  const renderPaymentDetailsSummary = () => {
    const waitMessage = paymentInProgress
      ? 'Please wait while we process your payment.'
      : 'Please wait while we check your payment methods.';
    return (
      <FormField>
        <Label>
          <Heading level={6}>Payment method</Heading>
        </Label>
        {(isLoading || !paymentMethod || paymentInProgress) && (
          <>
            <StyledAlertText>
              <Text css={{ paddingRight: 'sm' }}>{waitMessage}</Text>
              <Spinner />
            </StyledAlertText>
          </>
        )}
        {successLoadingPayments && paymentMethod && (
          <Box>
            {formatPaymentMethod(paymentMethod)}
            <TermsAndConditions
              paymentType={paymentMethod.paymentType}
              setAcceptedTerms={setAcceptedTerms}
              disabled={paymentInProgress}
              user={user}
            />
          </Box>
        )}
      </FormField>
    );
  };

  const renderErrorMessage = () => (
    <Page.Content>
      <Alert tone="critical">{errorMessage}</Alert>
    </Page.Content>
  );

  const handleBack = () => {
    emitGuestPaySubmitBackEvent({ user });
    history.push(manifest.guestPayPaymentDetailsPage);
  };

  const makePayment = (): void => {
    if (!acceptedTerms) {
      setPaymentInProgress(false);
      return;
    }
    setErrorMessage('');
    emitGuestPaySubmitPaymentEvent({ user });
    AdproductsUserBillingInvoicePaymentApiHandler_makeMultiPaymentForGuestInvoices({
      guestPaymentSessionId: guestPayState.guestPaySessionKey,
      paymentMethodId: guestPayState.paymentMethodId || '',
    })
      .then((result: RentalManagerUserMetroidAlicornGuestPaymentDto) => {
        setPaymentInProgress(false);
        // Store payment results in state
        updateGuestPayState({
          paymentResults: result.mappedInvoiceMetroidAlicornPaymentResults,
        });

        if (result.allPaymentsSucceeded) {
          // All payments succeeded, proceed to confirmation
          return history.push(manifest.guestPayConfirmationPage);
        } else {
          // Check if any payments succeeded (partial success)
          const hasAnySuccess = Object.values(result.mappedInvoiceMetroidAlicornPaymentResults).some(
            (paymentResult) => paymentResult.paymentSuccess,
          );

          if (hasAnySuccess) {
            // Partial success - proceed to confirmation page to show breakdown
            return history.push(manifest.guestPayConfirmationPage);
          } else {
            // All payments failed
            return setErrorMessage('Unable to make payment. Please try again later.');
          }
        }
      })
      .catch(() => {
        setPaymentInProgress(false);
        setErrorMessage('Unable to make payment. Please try again later.');
      });
  };

  return (
    <>
      <PageWrapper
        title="Submit your payment"
        subTitle={PAYMENT_INFO_SUBTITLE}
        config={DetailsPageWrapperConfig}
        configContent={DetailsPageWrapperConfigContent}
        alert={errorMessage ? renderErrorMessage() : undefined}
      >
        <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <StyledPaymentFormContainer>{renderPaymentDetailsSummary()}</StyledPaymentFormContainer>
          <StyledSummaryContainer>
            <InvoiceSummaryAmountList invoices={guestPayState.invoices} />
          </StyledSummaryContainer>
        </Box>
        <Box css={{ marginBlock: 'sm' }}>
          <GuestPayFooter
            handleBackButton={handleBack}
            handleNext={() => {
              setPaymentInProgress(true);
              makePayment();
            }}
            isNextDisabled={disableNextButton}
            customNextText={'Submit payment'}
          />
        </Box>
      </PageWrapper>
    </>
  );
};
